/**
 * Olm sessions to other devices: the pairwise channel a room key travels on.
 *
 * Establishing one costs a claimed prekey from the recipient, so sessions are
 * cached and reused. The wire shape is fixed by the phone and must not drift:
 *
 *   { "type": 0|1, "body": "<olm ciphertext>", "sender_key": "<our curve25519>" }
 *
 * `sender_key` is how the recipient works out which of its sessions to open
 * the message with, so an envelope without it is undeliverable.
 */
import { loadCrypto } from '@nsibi/e2ee-wasm';

import { api } from '../lib/api';
import { loadAccount, loadOlmSession, saveOlmSession } from './store';

export type DeviceKey = {
  userId: number;
  deviceId: string;
  curve25519: string;
  ed25519: string;
};

type QueryResponse = {
  device_keys: Record<
    string,
    Record<string, { device_id: string; curve25519_key: string; ed25519_key: string }>
  >;
};

type ClaimResponse = {
  one_time_keys: Array<{
    user_id: number;
    device_id: string;
    key?: string;
    fallback?: boolean;
  }>;
};

/** Every device belonging to the given users, as the directory sees them. */
export async function queryDevices(userIds: number[]): Promise<DeviceKey[]> {
  if (userIds.length === 0) return [];
  const res = await api<QueryResponse>(
    `/v1/keys/query/?user_ids=${userIds.join(',')}`,
  );
  const out: DeviceKey[] = [];
  for (const [userId, devices] of Object.entries(res.device_keys ?? {})) {
    for (const [deviceId, k] of Object.entries(devices)) {
      out.push({
        userId: Number(userId),
        deviceId,
        curve25519: k.curve25519_key,
        ed25519: k.ed25519_key,
      });
    }
  }
  return out;
}

/**
 * Olm-encrypt a payload to one device, creating the session if needed.
 *
 * Returns null when no session could be established - the device published
 * no keys, or its prekey pool and fallback are both gone. One unreachable
 * device must not fail the whole fan-out: the others still get the key, and
 * that device can ask for it later.
 */
export async function encryptOlmTo(
  device: DeviceKey,
  payload: unknown,
): Promise<string | null> {
  const core = await loadCrypto();
  const account = await loadAccount();
  if (!account) return null;

  const ourKeys = core.accountIdentityKeys(account);
  const plaintext = JSON.stringify(payload);

  let sessionPickle = await loadOlmSession(device.curve25519);

  if (!sessionPickle) {
    const claim = await api<ClaimResponse>('/v1/keys/claim/', {
      method: 'POST',
      body: { targets: [{ user_id: device.userId, device_id: device.deviceId }] },
    });
    const oneTimeKey = claim.one_time_keys?.[0]?.key;
    if (!oneTimeKey) return null;

    const created = core.createOutboundSession(
      account,
      device.curve25519,
      oneTimeKey,
    );
    sessionPickle = created.session_pickle;
  }

  const encrypted = core.sessionEncrypt(sessionPickle, plaintext);
  await saveOlmSession(device.curve25519, encrypted.session_pickle);

  return JSON.stringify({
    type: encrypted.message.type,
    body: encrypted.message.body,
    sender_key: ourKeys.curve25519,
  });
}
